import { FAMILY_SELF_CREATE_ENABLED } from "./featureFlags.ts";

/**
 * The two hosts this app has to know about, read once from the build's environment.
 *
 * **Both are configuration, not constants.** They differ per environment, and the repo's
 * split is that Terraform owns configuration and the deploy owns content: the platform
 * stack publishes each to SSM and this app's CD injects them (`stytch.tf`,
 * `.github/workflows/apps-my-cd.yml`).
 *
 * **They fail closed, and `null` is a state the app renders rather than a crash.** A bundle
 * built without one of these cannot work — every request would go to `undefined/app/...` —
 * so `main.tsx` shows the calm config-error card instead, exactly as it does for a missing
 * Stytch public token. The alternative, a throw at module load, is a white screen.
 */

/** Trim a trailing slash so `${origin}${path}` never produces `//app`. */
function origin(value: string | undefined): string | null {
  if (value === undefined || value.length === 0) return null;
  return value.replace(/\/+$/, "");
}

/** Where `/app/*` lives — `https://mcp[-dev].iamboone.app`. */
export const MCP_ORIGIN = origin(import.meta.env.VITE_MCP_ORIGIN);

/**
 * Where signing in happens — `https://login[-dev].iamboone.app`.
 *
 * This host runs no sign-in flow (src/stytch.ts), so a visitor with no session is sent
 * there and returned by `?return_to=`, which the login app validates against
 * `*.iamboone.app` before honouring it.
 */
export const LOGIN_ORIGIN = origin(import.meta.env.VITE_LOGIN_ORIGIN);

/**
 * The sign-in URL for whatever the browser is currently looking at.
 *
 * Built from `window.location.href` so a member who followed a deep link into a family
 * comes back to it rather than to the panel's root. The login app is what decides whether
 * this URL is safe to return to — it honours only a first-party `https://*.iamboone.app` —
 * so a value from this bundle is never trusted there either.
 */
export function signInUrl(returnTo: string = window.location.href): string | null {
  if (LOGIN_ORIGIN === null) return null;
  return `${LOGIN_ORIGIN}/login?return_to=${encodeURIComponent(returnTo)}`;
}

/**
 * Where a visitor with **no account yet** goes to start a family — or `null` when this build
 * cannot offer that at all.
 *
 * **`null` for two different reasons, and the caller treats them the same way: show nothing.**
 * Either the login origin is missing (a misconfigured build, which `main.tsx` has already
 * turned into the config-error card) or `family_self_create_enabled` is off in this
 * environment, in which case there is genuinely nowhere to send them and the login app would
 * answer "not open yet".
 *
 * **It names the login host, and that is the same handoff `signInUrl` already is.** ADR 0015
 * decision 2 is about the product's *published guidance* — `get_started`, the marketing CTA,
 * `connect.md` — which must name the apex so the login host is spelled in exactly one place a
 * family ever reads. This is one first-party app handing off to another, through configuration
 * that arrives from SSM. Pointing at the apex instead would need a fifth build input and would
 * route a dev visitor to `iamboone.app/create-family`, which 302s to the **prod** login host.
 *
 * **No `return_to`.** `/create-family` does not read one: it ends at the connect instructions,
 * because connecting Boone inside Claude or ChatGPT is the real next step for somebody who has
 * just become an owner (ADR 0015 decision 3). A parameter that was silently ignored would be
 * worse than its absence.
 */
export function startFamilyUrl(): string | null {
  if (LOGIN_ORIGIN === null || !FAMILY_SELF_CREATE_ENABLED) return null;
  return `${LOGIN_ORIGIN}/create-family`;
}
